import { useCallback, useState } from 'react';
import type * as z from 'zod';

// Recursive type that allows for nested objects and arrays while keeping JSON-serializable
type ValidStateTypes =
  | number
  | string
  | boolean
  | null
  | undefined
  | Array<ValidStateTypes>
  | { [key: string]: ValidStateTypes };

export function useStorageState<TState extends ValidStateTypes>(
  sessionKey: string,
  storage: Storage,
  schema: z.ZodType<TState>,
  initialState: () => TState,
) {
  // Try reading from session storage, fall back to initial state
  const [state, setStateInternal] = useState(() => {
    const storedState = storage.getItem(sessionKey);
    if (storedState) {
      try {
        return schema.parse(JSON.parse(storedState));
      } catch (e) {
        console.error('Error parsing stored state', e);
      }
    }

    return initialState();
  });

  const setState = useCallback(
    (newStateOrUpdater: TState | ((prev: TState) => TState)) => {
      setStateInternal(currentState => {
        const newState =
          typeof newStateOrUpdater === 'function'
            ? (newStateOrUpdater as (prev: TState) => TState)(currentState)
            : newStateOrUpdater;

        try {
          storage.setItem(sessionKey, JSON.stringify(newState));
        } catch (e) {
          console.error('Error setting state', e);
        }
        return newState;
      });
    },
    [sessionKey, storage],
  );

  return [state, setState] as const;
}
